import { normalizeIesire } from "./dirtyState";
import DocumentToolbar, { confirmDocumentLeave } from "../common/DocumentToolbar";
import useInvoiceSaveKey from "../../hooks/useInvoiceSaveKey";
import React, { useMemo, useState, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Container, Alert } from "react-bootstrap";
import { toast } from "react-toastify";
import { authFetch } from "../utils/authFetch";
import IesireForm, { emptyIesire } from "./Form";
import { changeIesireDocument } from "../utils/documentClassification";

// Shared comparison of editable fields only.
const hash = (obj) => JSON.stringify(obj);

export default function IesireCreate() {
  const saveKey = useInvoiceSaveKey();
  const nav = useNavigate();
  const location = useLocation();
  const saveInFlight = useRef(false);
  const [searchParams] = useSearchParams();
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const bxsoldint = searchParams.get("bxsoldint");
  const contId = searchParams.get("cont_id");
  const fromContor = searchParams.get("from_contor") === "1";
  const fromSantier = searchParams.get("from_santier") === "1";
  const fixedDocumentType = fromContor || fromSantier || searchParams.get("from_deviz") === "1" 
    ? "Factura" 
    : (searchParams.get("tip_document") === "Aviz" ? "Aviz" : null);

  const [initialForm] = useState(() => {
    const initial = { ...emptyIesire(), cont_id: Number(contId) || null };
    if (fromContor && location.state?.contorInvoice) Object.assign(initial, location.state.contorInvoice);
    if (fromSantier && location.state?.santierInvoice) Object.assign(initial, location.state.santierInvoice);
    
    if (searchParams.get("from_deviz") === "1") {
      try {
        const deviz = location.state?.devizInvoice || JSON.parse(localStorage.getItem("facturare_deviz") || "{}");
        if (deviz.contact_id) {
          initial.contact_id = Number(deviz.contact_id);
          initial.contact_label = deviz.contact_label || "";
        }
        if (deviz.numar_comanda) {
          initial.comanda_client = deviz.numar_comanda;
        }
        if (deviz.deviz_id) {
          initial.deviz_id = deviz.deviz_id;
          if (deviz.print_details) initial.print_details = {...deviz.print_details};
        }
        if (deviz.observatii) {
          initial.observatii = deviz.observatii;
        }
        initial.tip_document = "Factura";
        if (deviz.lines && deviz.lines.length > 0) {
          initial.linii = deviz.lines.map(l => ({
            descriere: l.descriere || "Linie deviz",
            qty: l.qty || 1,
            price: l.price || 0,
            vat_rate: l.vat_rate || 19,
            bxpart_id: l.bxpart_id || null,
            bxpart_label: l.bxpart_label || "",
          }));
        } else if (deviz.valoare) {
          initial.linii = [
            {
              descriere: deviz.descriere || "Reparatie conform deviz",
              qty: 1,
              price: Number(deviz.valoare) || 0,
              vat_rate: 19,
            }
          ];
        }
      } catch (e) {
        console.error("Error reading facturare_deviz", e);
      }
    }

    return fixedDocumentType ? changeIesireDocument(initial, fixedDocumentType) : initial;
  });
  const [form, setForm] = useState(initialForm);

  const originalHash = useMemo(
    () => hash(normalizeIesire(initialForm)),
    [initialForm],
  );
  const currentHash = useMemo(() => hash(normalizeIesire(form)), [form]);
  const isDirty = originalHash !== currentHash;


  const handleSave = async (customFormPayload = null) => {
    if (saveInFlight.current) return;
    saveInFlight.current = true;
    setErr("");
    setSaving(true);
    const candidate = customFormPayload || form;
    const formToSubmit = fixedDocumentType
      ? { ...candidate, tip_document: fixedDocumentType, tip_factura_iesire_id: null, tip_factura_iesire_label: "" }
      : candidate;
    try {
      const saved = await authFetch("/iesire/", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Idempotency-Key": saveKey.get() },
        body: JSON.stringify({ ...formToSubmit, contor_facturare: initialForm.contor_facturare, san_situatie_id: initialForm.san_situatie_id, san_versiune: initialForm.san_versiune, auto_number: !!formToSubmit.deviz_id || fromSantier || fromContor }),
      });
      toast.success("Iesirea a fost creata cu succes!");
      if (fromSantier) {
        nav(`/iesire/${saved.id}`, { replace: true });
      } else if (formToSubmit.deviz_id) {
        localStorage.removeItem("facturare_deviz");
        nav("/comenzi/intern/linii", { replace: true });
      } else {
        nav(`/iesire/${saved.id}`, { replace: true });
      }
      return saved;
    } catch (e) {
      setErr(e.message);
      toast.error(`Eroare la crearea iesirii: ${e.message}`);
      throw e;
    } finally {
      setSaving(false);
      saveInFlight.current = false;
    }
  };

  if (fromContor && !initialForm.contor_facturare) return <Container className="py-3"><Alert variant="warning">Reveniți la CONTOR → Contracte pentru previzualizarea facturii.</Alert></Container>;
  if (fromSantier && !initialForm.san_situatie_id) return <Container className="py-3"><Alert variant="warning">Reveniți la situația de lucrări pentru a pregăti factura.</Alert><button className="btn btn-outline-secondary" onClick={() => nav("/santier?tab=situatii")}>Înapoi la situații</button></Container>;

  return (
    <Container className="py-3">
      <DocumentToolbar
        title={fixedDocumentType === "Aviz" ? "Creează aviz client" : bxsoldint === "1" ? "Factură inițială client" : "Creează ieșire"}
        dirty={isDirty} newDocument error={err} busy={saving}
        onBack={() => {
          if (fromSantier) return nav("/santier?tab=situatii", { replace: true });
          if (initialForm.deviz_id) return nav("/comenzi/intern/linii", { replace: true });
          if (contId) nav(`/iesire/iesire_list/${contId}${bxsoldint === "1" ? "?bxsoldint=1" : ""}`);
          else nav(-1);
        }}
        onSave={() => window.dispatchEvent(new Event("iesire-submit"))}
        saveDisabled={!isDirty && !fromSantier}
      />

      {err && <Alert variant="danger">{err}</Alert>}
      {fromContor && <Alert variant="info">Factură CONTOR: articolele de servicii și contul de client sunt preluate din previzualizare. Păstrați valorile calculate. Numărul se atribuie la salvare. <button className="btn btn-link" onClick={() => { if (confirmDocumentLeave(isDirty)) nav(`/contor/contracte/facturare/${initialForm.contor_facturare.contract_id}`); }}>Înapoi la contract</button></Alert>}
      {fromSantier && <Alert variant="info">Factură din situația SL-{initialForm.san_situatie_id}. Fiecare poziție facturează valoarea unei lucrări; cantitatea executată și U.M. sunt în descriere. Păstrați liniile preluate. Numărul se atribuie la salvare, iar factura se finalizează automat. <button className="btn btn-link" onClick={() => { if (confirmDocumentLeave(isDirty)) nav("/santier?tab=situatii"); }}>Înapoi la situații</button></Alert>}

      <IesireForm
        fixedDocumentType={fixedDocumentType}
        initial={initialForm}
        onSave={handleSave}
        busy={saving}
        showActions={false}
        onCancel={fromSantier ? () => { if (confirmDocumentLeave(isDirty)) nav("/santier?tab=situatii", { replace: true }); } : initialForm.deviz_id ? () => { if (confirmDocumentLeave(isDirty)) nav("/comenzi/intern/linii", { replace: true }); } : undefined}
        onChangePayload={(p) => setForm(p)}
      />
    </Container>
  );
}
